import styled, { css, keyframes } from 'styled-components';
import COLORS from '../../utils/Colors';
import { Link } from 'react-router-dom';

// ============================================
// ANIMATIONS
// ============================================
const fadeIn = keyframes`
    from { opacity: 0; }
    to { opacity: 1; }
`;

const slideUp = keyframes`
    from {
        opacity: 0;
        transform: translateY(100%);
    }
    to {
        opacity: 1;
        transform: translateY(0);
    }
`;

const scaleIn = keyframes`
    from {
        opacity: 0;
        transform: scale(0.95);
    }
    to {
        opacity: 1;
        transform: scale(1);
    }
`;

// ============================================
// INTERFACES
// ============================================
interface ButtonProps {
    isBtnDisabled: boolean;
}

interface SectionProps {
    focus: boolean;
    flex?: number;
    scrollX?: boolean;
}

interface MobileCardValueProps {
    hasValue?: boolean;
}

// ============================================
// DESKTOP SEARCH BAR
// ============================================
export const Container = styled.div`
    display: flex;
    position: relative;
    background-color: ${COLORS.white};
    height: 4rem;
    width: 90%;
    max-width: 52rem;
    border-radius: 1.5rem;
    border: 1px solid #e5e7eb;
    box-shadow: 0 10px 20px -5px rgba(0, 0, 0, 0.06), 
                0 4px 6px -2px rgba(0, 0, 0, 0.04);
    z-index: 50;
    overflow: visible;
    align-items: center;

    @media (max-width: 767px) {
        display: none;
    }
`;

export const Section = styled.div<SectionProps>`
    display: flex;
    flex: ${props => props.flex !== undefined ? props.flex : 1};
    flex-direction: column;
    justify-content: center;
    gap: 0.1rem;
    cursor: text;
    height: 100%;
    transition: background-color 200ms;

    &:hover {
        background-color: #f9fafb;
    }

    ${props => props.scrollX === true && css`
        -ms-overflow-style: none;
        scrollbar-width: none;
        overflow-x: overlay;
        ::-webkit-scrollbar { width: 0 !important }
    `}

    ${props => props.focus === true && css`
        background-color: #f9fafb;
        outline: 2px solid #f3e8ff;
        outline-offset: -2px;
    `}
`;

export const Label = styled.label`
    flex: 1;
    font-size: 0.625rem;
    color: #6b7280;
    font-weight: 700;
    text-transform: uppercase;
    letter-spacing: 0.05em;
    cursor: pointer;
    margin-bottom: 0.125rem;
`;

export const Input = styled.input`
    flex: 1;
    background: transparent;
    border: none;
    font-size: 0.875rem;
    font-weight: 500;
    color: #111827;
    height: auto;

    &::placeholder {
        font-size: 0.875rem;
        color: #9ca3af;
        font-weight: 400;
    }

    &:focus {
        outline: none;
    }
`;

export const Row = styled.div`
    display: flex;
    padding-left: 1.5rem;
    padding-right: 0.5rem;
    align-items: center;
`;

export const IconWrapper = styled.div`
    display: flex;
    align-items: center;
    margin-right: 0.75rem;
    color: #9ca3af;
    flex-shrink: 0;
`;

export const SearchBtnContainer = styled.div`
    display: flex;
    justify-content: center;
    align-items: center;
    padding-right: 0.5rem;
    cursor: pointer;
`;

export const SearchBtn = styled.div<ButtonProps>`
    display: flex;
    border-radius: 1rem;
    background-color: #000;
    padding: 0.75rem 2rem;
    justify-content: center;
    align-items: center;
    cursor: pointer;
    transition: all 200ms;

    &:hover {
        background-color: #1f2937;
        transform: scale(1.05);
    };

    &:active {
        transform: scale(0.95);
    }

    & div {
        align-items: center;
        display: flex;
    }

    & img {
        width: 0;
        height: 0;
        display: none;
    }

    & p {
        color: ${COLORS.white};
        font-size: 0.875rem;
        font-weight: 600;
    }
`;

export const Separator = styled.div`
    display: flex;
    height: 100%;
    align-items: center;
`;

export const SeparatorLine = styled.div`
    display: flex;
    height: 2.5rem;
    width: 1px;
    background-color: #e5e7eb;
`;

export const StyledLink = styled(Link)`
    display: flex;
    text-decoration: none;
    width: 100%;

    @media (min-width: 768px) {
        width: auto;
    }
`;

// ============================================
// SUGGESTION BOXES (Desktop)
// ============================================
export const ResultsBox = styled.div`
    display: flex;
    flex-direction: column;
    background-color: ${COLORS.white};
    position: absolute;
    top: 5.5rem;
    left: 0;
    width: 100%;
    max-width: 320px;
    border-radius: 30px;
    box-shadow: 0 0 10px ${COLORS.rgba000000000014};
    z-index: 100;
`;

export const CalendarContainer = styled.div`
    display: flex;
    flex-direction: column;
    background-color: ${COLORS.white};
    position: absolute;
    top: 5.5rem;
    left: 50%;
    transform: translateX(-50%);
    border-radius: 16px;
    box-shadow: 0 6px 20px rgba(0, 0, 0, 0.08), 0 0 0 1px rgba(0, 0, 0, 0.04);
    z-index: 1000;
    padding: 24px;
    min-width: 320px;
    max-width: calc(100vw - 2rem);
    animation: ${scaleIn} 0.2s cubic-bezier(0.16, 1, 0.3, 1);

    .rdp {
        --rdp-cell-size: 48px;
        --rdp-accent-color: #00A699;
        --rdp-background-color: #e6f9f7;
        margin: 0;
    }

    .rdp-months {
        display: flex;
        gap: 24px;
        justify-content: center;
    }

    .rdp-caption {
        display: flex;
        justify-content: center;
        padding: 0 0 16px 0;
        position: relative;
    }

    .rdp-caption_label {
        font-size: 16px;
        font-weight: 600;
        color: #222222;
        text-transform: capitalize;
    }

    .rdp-nav {
        position: absolute;
        top: 0;
        width: 100%;
        display: flex;
        justify-content: space-between;
        pointer-events: none;
    }

    .rdp-nav_button {
        pointer-events: auto;
        width: 32px;
        height: 32px;
        border-radius: 50%;
        border: 1px solid #e0e0e0;
        background-color: ${COLORS.white};
        color: #222222;
        cursor: pointer;
        transition: all 0.15s ease;
    }

    .rdp-nav_button:hover:not(:disabled) {
        background-color: #f7f7f7;
        border-color: #d0d0d0;
        transform: scale(1.05);
    }

    .rdp-nav_button:disabled {
        opacity: 0.3;
        cursor: not-allowed;
    }

    .rdp-head_cell {
        font-size: 12px;
        font-weight: 600;
        color: #717171;
        text-transform: uppercase;
        padding: 8px 0;
    }

    .rdp-cell {
        padding: 2px;
    }

    .rdp-button {
        width: var(--rdp-cell-size);
        height: var(--rdp-cell-size);
        border-radius: 50%;
        border: none;
        background: transparent;
        font-size: 14px;
        font-weight: 500;
        color: #222222;
        cursor: pointer;
        transition: all 0.15s ease;
    }

    .rdp-day_today:not(.rdp-day_selected) .rdp-button {
        font-weight: 700;
        position: relative;
    }

    .rdp-day_today:not(.rdp-day_selected) .rdp-button::after {
        content: '';
        position: absolute;
        bottom: 6px;
        left: 50%;
        transform: translateX(-50%);
        width: 4px;
        height: 4px;
        border-radius: 50%;
        background-color: #00A699;
    }

    .rdp-day:not(.rdp-day_selected):not(.rdp-day_disabled) .rdp-button:hover {
        background-color: #f0f9f8;
        transform: scale(1.05);
    }

    .rdp-day_selected .rdp-button {
        background-color: #00A699 !important;
        color: ${COLORS.white} !important;
        font-weight: 600;
    }

    .rdp-day_selected .rdp-button:hover {
        background-color: #008c82 !important;
    }

    .rdp-day_disabled .rdp-button {
        color: #dddddd;
        cursor: not-allowed;
        text-decoration: line-through;
    }

    .rdp-day_outside .rdp-button {
        color: #cccccc;
    }

    @media (max-width: 768px) {
        padding: 16px;
        min-width: 280px;
        border-radius: 12px;

        .rdp {
            --rdp-cell-size: 40px;
        }

        .rdp-months {
            flex-direction: column;
            gap: 0;
        }
    }

    @media (min-width: 769px) {
        min-width: 660px;
        max-width: 700px;

        .rdp-months {
            gap: 40px;
        }
    }
`;

export const ResultsContainer = styled.div`
    background-color: ${COLORS.white};
    flex: 1;
    border-radius: 30px;
    padding-bottom: 1.5rem;
`;

export const ResultsItem = styled.div`
    cursor: pointer;
    padding: 0.9rem 0;
    
    &:hover {
        background-color: ${COLORS.hsl000000098};
    }

    & p {
        color: ${COLORS.black};
        font-size: 0.875rem;
        margin: 0;
        padding-left: 1.5rem;
    }
`;

export const SuggestBoxTitle = styled.h4`
    font-size: 0.9rem;
    font-weight: 700;
    margin-left: 1.5rem;
    color: ${COLORS.black};
`;

export const InputCategoriesContainer = styled.div`
    display: flex;
    flex-direction: row;
`;

export const PlaceHolderText = styled.p`
    font-size: 1em;
    color: ${COLORS.hsl000000056};
    font-weight: 400;
`;

export const LocationBox = styled.div`
    display: flex;
    flex-direction: column;
    background-color: ${COLORS.white};
    position: absolute;
    top: 5.5rem;
    right: 0;
    width: 100%;
    max-width: 320px;
    border-radius: 30px;
    box-shadow: 0 0 10px ${COLORS.rgba000000000014};
    z-index: 100;
`;

// ============================================
// MOBILE SEARCH PILL (Collapsed State)
// ============================================
export const MobileSearchPill = styled.button`
    /* CRITICAL: Ensure the pill displays correctly */
    display: flex !important;
    align-items: center;
    width: 100%;
    min-height: 56px;
    padding: 8px 8px 8px 16px;
    background: ${COLORS.white};
    border: 1px solid #e5e7eb;
    border-radius: 32px;
    box-shadow: 
        0 4px 12px rgba(0, 0, 0, 0.08),
        0 2px 4px rgba(0, 0, 0, 0.04),
        0 0 0 1px rgba(0, 0, 0, 0.02);
    cursor: pointer;
    transition: all 0.2s ease;
    gap: 12px;
    overflow: hidden;
    box-sizing: border-box;

    /* Reset button defaults */
    font-family: inherit;
    -webkit-appearance: none;
    -moz-appearance: none;
    appearance: none;

    &:hover {
        box-shadow: 
            0 8px 24px rgba(0, 0, 0, 0.12),
            0 4px 8px rgba(0, 0, 0, 0.06);
        transform: translateY(-1px);
    }

    &:active {
        transform: translateY(0) scale(0.99);
        box-shadow: 
            0 2px 8px rgba(0, 0, 0, 0.08),
            0 1px 2px rgba(0, 0, 0, 0.04);
    }

    @media (min-width: 768px) {
        display: none !important;
    }
`;

export const MobileSearchPillIcon = styled.div`
    display: flex;
    align-items: center;
    justify-content: center;
    width: 40px;
    height: 40px;
    background: linear-gradient(135deg, #111827 0%, #374151 100%);
    border-radius: 50%;
    color: ${COLORS.white};
    flex-shrink: 0;

    svg {
        width: 18px;
        height: 18px;
    }
`;

export const MobileSearchPillText = styled.div`
    flex: 1;
    min-width: 0; /* Critical for text truncation */
    text-align: left;
`;

export const MobileSearchPillTitle = styled.span`
    display: block;
    font-size: 15px;
    font-weight: 600;
    color: #111827;
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
    line-height: 1.3;
`;

export const MobileSearchPillSubtitle = styled.span`
    display: block;
    font-size: 12px;
    font-weight: 400;
    color: #6b7280;
    margin-top: 1px;
`;

export const MobileSearchPillButton = styled.div`
    display: flex;
    align-items: center;
    justify-content: center;
    gap: 6px;
    background: linear-gradient(135deg, #111827 0%, #1f2937 100%);
    color: ${COLORS.white};
    padding: 10px 18px;
    border-radius: 24px;
    font-size: 14px;
    font-weight: 600;
    flex-shrink: 0;
    transition: all 0.2s ease;
    white-space: nowrap;

    svg {
        width: 16px;
        height: 16px;
    }

    &:hover {
        background: linear-gradient(135deg, #1f2937 0%, #374151 100%);
    }
`;

// ============================================
// MOBILE MODAL (Full-screen)
// ============================================
export const MobileModalOverlay = styled.div`
    position: fixed;
    inset: 0;
    background: rgba(0, 0, 0, 0.5);
    z-index: 9999;
    animation: ${fadeIn} 0.2s ease;
    -webkit-backdrop-filter: blur(4px);
    backdrop-filter: blur(4px);
`;

export const MobileModal = styled.div`
    position: fixed;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    width: 100%;
    height: 100%;
    /* Use dvh for dynamic viewport height on mobile */
    height: 100dvh;
    background: #f9fafb;
    display: flex;
    flex-direction: column;
    z-index: 10000;
    animation: ${slideUp} 0.35s cubic-bezier(0.16, 1, 0.3, 1);

    /* Fallback for browsers without dvh */
    @supports not (height: 100dvh) {
        height: 100vh;
        height: -webkit-fill-available;
    }
`;

export const MobileModalHeader = styled.div`
    display: flex;
    align-items: center;
    justify-content: space-between;
    padding: 12px 16px;
    background: ${COLORS.white};
    border-bottom: 1px solid #e5e7eb;
    flex-shrink: 0;
    min-height: 56px;
`;

export const MobileBackButton = styled.button`
    display: flex;
    align-items: center;
    justify-content: center;
    width: 40px;
    height: 40px;
    border: none;
    background: transparent;
    color: #111827;
    cursor: pointer;
    border-radius: 50%;
    transition: background 0.15s ease;
    -webkit-tap-highlight-color: transparent;

    &:hover, &:active {
        background: #f3f4f6;
    }

    svg {
        width: 24px;
        height: 24px;
    }
`;

export const MobileModalTitle = styled.h2`
    font-size: 17px;
    font-weight: 600;
    color: #111827;
    margin: 0;
    letter-spacing: -0.01em;
`;

export const MobileModalContent = styled.div`
    flex: 1;
    overflow-y: auto;
    padding: 20px 16px;
    -webkit-overflow-scrolling: touch;
    overscroll-behavior: contain;
`;

export const MobileModalFooter = styled.div`
    padding: 16px;
    background: ${COLORS.white};
    border-top: 1px solid #e5e7eb;
    flex-shrink: 0;
    /* Safe area for devices with home indicator */
    padding-bottom: max(16px, env(safe-area-inset-bottom));
`;

// ============================================
// MOBILE SEARCH CARDS (Main View)
// ============================================
export const MobileSearchCard = styled.div`
    display: flex;
    align-items: center;
    padding: 16px;
    background: ${COLORS.white};
    border: 1px solid #e5e7eb;
    border-radius: 16px;
    margin-bottom: 12px;
    cursor: pointer;
    transition: all 0.15s ease;
    -webkit-tap-highlight-color: transparent;

    &:hover {
        border-color: #d1d5db;
        box-shadow: 0 2px 8px rgba(0, 0, 0, 0.04);
    }

    &:active {
        background: #f9fafb;
        transform: scale(0.99);
    }
`;

export const MobileSearchButtonWrapper = styled.div`
    margin-top: 24px;
    padding-bottom: 40px; /* Extra padding for scroll comfort */
`;

export const MobileCardIcon = styled.div`
    display: flex;
    align-items: center;
    justify-content: center;
    width: 44px;
    height: 44px;
    background: linear-gradient(135deg, #f3f4f6 0%, #e5e7eb 100%);
    border-radius: 12px;
    color: #6b7280;
    flex-shrink: 0;
    margin-right: 14px;

    svg {
        width: 20px;
        height: 20px;
    }
`;

export const MobileCardContent = styled.div`
    flex: 1;
    min-width: 0;
`;

export const MobileCardLabel = styled.div`
    font-size: 11px;
    font-weight: 700;
    color: #9ca3af;
    text-transform: uppercase;
    letter-spacing: 0.06em;
    margin-bottom: 4px;
`;

export const MobileCardValue = styled.div<MobileCardValueProps>`
    font-size: 15px;
    font-weight: ${props => props.hasValue ? '500' : '400'};
    color: ${props => props.hasValue ? '#111827' : '#9ca3af'};
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
    line-height: 1.4;
`;

export const MobileCardClear = styled.button`
    display: flex;
    align-items: center;
    justify-content: center;
    width: 32px;
    height: 32px;
    border: none;
    background: #f3f4f6;
    border-radius: 50%;
    color: #6b7280;
    cursor: pointer;
    margin-left: 12px;
    flex-shrink: 0;
    transition: all 0.15s ease;
    -webkit-tap-highlight-color: transparent;

    &:hover, &:active {
        background: #e5e7eb;
        color: #374151;
    }

    svg {
        width: 16px;
        height: 16px;
    }
`;

// ============================================
// MOBILE STEP CONTENT (Category/Location/Date)
// ============================================
export const MobileStepContent = styled.div`
    display: flex;
    flex-direction: column;
`;

export const MobileInputWrapper = styled.div`
    display: flex;
    align-items: center;
    padding: 14px 16px;
    background: ${COLORS.white};
    border: 2px solid #e5e7eb;
    border-radius: 14px;
    margin-bottom: 20px;
    color: #9ca3af;
    transition: all 0.2s ease;

    &:focus-within {
        border-color: #111827;
        background: ${COLORS.white};
        box-shadow: 0 0 0 4px rgba(17, 24, 39, 0.08);
    }

    svg {
        width: 20px;
        height: 20px;
        flex-shrink: 0;
    }
`;

export const MobileInput = styled.input`
    flex: 1;
    border: none;
    background: transparent;
    font-size: 16px; /* Prevents iOS zoom */
    font-weight: 500;
    color: #111827;
    margin-left: 12px;
    outline: none;
    -webkit-appearance: none;

    &::placeholder {
        color: #9ca3af;
        font-weight: 400;
    }
`;

export const MobileSelectedPills = styled.div`
    display: flex;
    flex-wrap: wrap;
    gap: 8px;
    margin-bottom: 20px;
`;

export const MobilePill = styled.div`
    display: inline-flex;
    align-items: center;
    padding: 8px 12px;
    background: linear-gradient(135deg, #f3e8ff 0%, #fce7f3 100%);
    border-radius: 20px;
    font-size: 14px;
    font-weight: 500;
    color: #7c3aed;
`;

export const MobilePillRemove = styled.button`
    display: flex;
    align-items: center;
    justify-content: center;
    width: 20px;
    height: 20px;
    border: none;
    background: transparent;
    color: #7c3aed;
    cursor: pointer;
    margin-left: 4px;
    padding: 0;
    -webkit-tap-highlight-color: transparent;

    &:hover, &:active {
        color: #5b21b6;
    }

    svg {
        width: 14px;
        height: 14px;
    }
`;

export const MobileResultsList = styled.div`
    display: flex;
    flex-direction: column;
    /* Extra padding at bottom for scroll */
    padding-bottom: 120px;
`;

export const MobileResultsTitle = styled.h3`
    font-size: 11px;
    font-weight: 700;
    color: #9ca3af;
    text-transform: uppercase;
    letter-spacing: 0.06em;
    margin: 0 0 12px 0;
    padding: 0 4px;
`;

export const MobileResultItem = styled.div`
    padding: 14px 16px;
    font-size: 16px;
    font-weight: 500;
    color: #111827;
    cursor: pointer;
    border-radius: 12px;
    transition: background 0.15s ease;
    -webkit-tap-highlight-color: transparent;

    &:hover {
        background: ${COLORS.white};
    }

    &:active {
        background: #e5e7eb;
    }
`;

export const MobileDatePickerWrapper = styled.div`
    display: flex;
    justify-content: center;
    padding: 16px 0;
    background: ${COLORS.white};
    border-radius: 16px;

    .rdp {
        --rdp-cell-size: 44px;
        --rdp-accent-color: #111827;
        --rdp-background-color: #f3f4f6;
        margin: 0;
    }

    .rdp-caption_label {
        font-size: 16px;
        font-weight: 600;
        color: #111827;
        text-transform: capitalize;
    }

    .rdp-nav_button {
        width: 36px;
        height: 36px;
        border-radius: 50%;
        border: 1px solid #e5e7eb;
        background: ${COLORS.white};
        color: #111827;
        transition: all 0.15s ease;
    }

    .rdp-nav_button:hover {
        background: #f3f4f6;
    }

    .rdp-head_cell {
        font-size: 12px;
        font-weight: 600;
        color: #6b7280;
        text-transform: uppercase;
    }

    .rdp-button {
        width: var(--rdp-cell-size);
        height: var(--rdp-cell-size);
        border-radius: 50%;
        font-size: 15px;
        font-weight: 500;
        transition: all 0.15s ease;
    }

    .rdp-day_selected .rdp-button {
        background: #111827 !important;
        color: ${COLORS.white} !important;
        font-weight: 600;
    }

    .rdp-day_today:not(.rdp-day_selected) .rdp-button {
        font-weight: 700;
        color: #111827;
    }

    .rdp-day:not(.rdp-day_selected):not(.rdp-day_disabled) .rdp-button:hover {
        background: #f3f4f6;
    }
`;

// ============================================
// MOBILE SEARCH BUTTON
// ============================================
export const MobileSearchButton = styled.button`
    display: flex;
    align-items: center;
    justify-content: center;
    gap: 10px;
    width: 100%;
    padding: 16px 24px;
    background: linear-gradient(135deg, #111827 0%, #1f2937 100%);
    color: ${COLORS.white};
    border: none;
    border-radius: 14px;
    font-size: 16px;
    font-weight: 600;
    cursor: pointer;
    transition: all 0.2s ease;
    -webkit-tap-highlight-color: transparent;
    -webkit-appearance: none;

    &:hover {
        background: linear-gradient(135deg, #1f2937 0%, #374151 100%);
        transform: translateY(-1px);
        box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15);
    }

    &:active {
        transform: translateY(0) scale(0.99);
    }

    svg {
        width: 20px;
        height: 20px;
    }
`;